import * as React from 'react';
import { Input, Select, Switch } from '@ixicare/ui';
import { t } from '~i18n';
import { Field, FieldValue, FieldValues, SubProfile } from '../schema';
import FieldLabel from './FieldLabel';
import NumberFieldControl from './NumberFieldControl';
import StatusBlockControl from './StatusBlockControl';
import PolygonControl from './PolygonControl';
import { isFieldEnabled } from '../utils/visibility';
import { applySubProfile, deriveSubProfile } from '../utils/subProfile';
import { GatingContext, canViewField } from '../utils/gating';
import FieldWrapper, { DifferingKeyDetail } from './FieldWrapper';

interface Props {
  field: Field;
  values: FieldValues;
  initialValue: FieldValue;
  initialValues: FieldValues;
  error?: string;
  onChange: (id: string, value: FieldValue) => void;
  gating: GatingContext;
  differingDetail?: Map<string, DifferingKeyDetail[]>;
}

const asSubProfile = (field: Field): SubProfile => ({
  label_key: field.title,
  options: (field.options ?? []).map((o) => ({
    value: String(o.value),
    label_key: o.label_key ?? String(o.value),
    props: o.props,
  })),
});

const toSelectOption = (o: NonNullable<Field['options']>[number]): { value: string; label: string } =>
  typeof o === 'string'
    ? { value: o, label: o }
    : {
        value: String(o.value),
        label: o.label_key ? t(o.label_key) : String(o.value),
      };

const FieldRenderer: React.FC<Props> = ({
  field,
  values,
  initialValue,
  initialValues,
  error,
  onChange,
  gating,
  differingDetail,
}) => {
  if (!canViewField(field, gating)) return null;

  const value = values[field.id];
  const disabled = !isFieldEnabled(field, values);
  const label = <FieldLabel field={field} />;
  const changed = value !== initialValue && initialValue !== undefined;

  let control: React.ReactNode = null;

  switch (field.type) {
    case 'TextField':
    case 'PasswordField':
    case 'TextArea':
    case 'CodeBlock':
      control = (
        <Input
          label={label}
          type={field.type === 'PasswordField' ? 'password' : 'text'}
          value={value == null ? '' : String(value)}
          placeholder={field.placeholder ?? ''}
          onValueChange={(v) => onChange(field.id, v)}
          disabled={disabled}
        />
      );
      break;
    case 'NumberField':
      control = (
        <NumberFieldControl
          field={field}
          value={value}
          label={label}
          onChange={(v) => onChange(field.id, v)}
          disabled={disabled}
        />
      );
      break;
    case 'DropDown': {
      const isSubProfile = (field.options ?? []).some((o) => o.props);
      if (isSubProfile) {
        const sub = asSubProfile(field);
        const selectedValue = deriveSubProfile(sub, values);
        control = (
          <Select
            label={label}
            value={selectedValue == null ? null : String(selectedValue)}
            options={(field.options ?? []).map(toSelectOption)}
            onChange={(v) => applySubProfile(sub, v, initialValues, onChange)}
            placeholder={field.placeholder}
            disabled={disabled}
          />
        );
        break;
      }

      const vals = (field.options ?? []).map((o) => (typeof o === 'string' ? o : o.value));

      const selected = vals.find((v) => v === value) ?? value;

      control = (
        <Select
          label={label}
          value={selected == null ? null : String(selected)}
          options={(field.options ?? []).map(toSelectOption)}
          onChange={(v) => {
            // Recover the typed value (number/boolean) the option declared.
            const original = vals.find((val) => String(val) === v);
            onChange(field.id, original !== undefined ? original : v);
          }}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      );
      break;
    }
    case 'ToggleSwitch':
      control = (
        <Switch label={label} checked={value === true} onChange={(v) => onChange(field.id, v)} disabled={disabled} />
      );
      break;
    case 'StatusBlock':
      control = <StatusBlockControl field={field} label={label} />;
      break;
    case 'Polygon':
      control = <PolygonControl value={value} label={label} />;
      break;
  }

  // return control;
  return (
    <FieldWrapper
      field={field}
      value={value}
      error={error}
      changed={changed && !field.highlight}
      bulk={differingDetail !== undefined}
      differing={differingDetail?.has(field.id) ?? false}
      differingDetail={differingDetail?.get(field.id)}
    >
      {control}
    </FieldWrapper>
  );
};

export default FieldRenderer;
